import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import { 
  Bot, 
  Users, 
  Shuffle, 
  Mic, 
  Clock, 
  Target, 
  Award, 
  Zap, 
  MessageSquare, 
  Edit3,
  ArrowRight,
  Play,
  User,
  Settings
} from 'lucide-react';
import BillTemplate from '../components/BillTemplate';
import DebateLayout from '../components/DebateLayout';
import { BentoCard, BentoGrid } from '../components/ui/debate-mode-grid';
import { cn } from '../lib/utils';

const Debate = () => {
  const { user } = useUser();
  const [selectedMode, setSelectedMode] = useState(null);
  const [showAIDebate, setShowAIDebate] = useState(false);
  const [showFriendDebate, setShowFriendDebate] = useState(false);
  const [showRandomDebate, setShowRandomDebate] = useState(false);

  // AI Debate state
  const [topic, setTopic] = useState('Social media should be regulated by the government');
  const [side, setSide] = useState('RANDOM');
  const [format, setFormat] = useState('LD');
  const [difficulty, setDifficulty] = useState('intermediate');
  const [mode, setMode] = useState('debate');
  const [session, setSession] = useState(null);
  const [stage, setStage] = useState('');
  const [stageCode, setStageCode] = useState(null);
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [stageDuration, setStageDuration] = useState(null);
  const [stageType, setStageType] = useState(null);
  const [userSide, setUserSide] = useState(null);
  const [botSide, setBotSide] = useState(null);
  const [frameworks, setFrameworks] = useState(null);
  const [log, setLog] = useState([]);
  const [timer, setTimer] = useState(90);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isInitializing, setIsInitializing] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [debateReady, setDebateReady] = useState(false);
  const [loadingStage, setLoadingStage] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [targetProgress, setTargetProgress] = useState(0); // Target progress for smooth animation
  const progressIntervalRef = useRef(null);
  const pendingDebateDataRef = useRef(null); // Store debate data while waiting for progress
  
  // Smoothly animate progress bar towards target progress
  useEffect(() => {
    if (isInitializing && progressIntervalRef.current === null) {
      // Start smooth progress animation towards targetProgress
      progressIntervalRef.current = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev < targetProgress) {
            // Smoothly animate towards target
            const diff = targetProgress - prev;
            const increment = diff > 10 ? 2 : diff > 5 ? 1 : 0.5; // Faster for larger gaps
            return Math.min(prev + increment, targetProgress);
          }
          return prev;
        });
      }, 50); // Update every 50ms for smooth animation
    } else if (!isInitializing && progressIntervalRef.current) {
      // Clear interval when not initializing
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    // Cleanup on unmount
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isInitializing, targetProgress]);
  
  // Start countdown when progress reaches 100% and we have debate data
  useEffect(() => {
    if (Math.round(loadingProgress) >= 100 && pendingDebateDataRef.current && isInitializing && countdown === null) {
      const data = pendingDebateDataRef.current;
      
      // Clear any progress animation
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      
      // Small delay to ensure progress bar shows 100% before countdown
      setTimeout(() => {
        // Start countdown
        startCountdown(() => {
          setIsInitializing(false);
          setSession(data.sessionId);
          setStage(data.stage);
          setStageCode(data.stageCode || null);
          setActiveSpeaker(data.activeSpeaker || null);
          setStageDuration(data.duration || null);
          setStageType(data.stageType || null);
          setUserSide(data.userSide || null);
          setBotSide(data.botSide || null);
          setFrameworks(data.frameworks || null);
          // For Model Congress, set initial transcript if available
          if (data.initialTranscript) {
            setLog(data.initialTranscript);
          }
          // Store delegates for Model Congress
          if (data.delegates) {
            setDelegates(data.delegates);
          }
          // Store selectedBill if available
          if (data.selectedBillData) {
            setSelectedBill(data.selectedBillData);
          }
          setTimer(data.duration || 90);
          setRunning(true);
          setDebateReady(true);
          setShowBillSelection(false);
          
          // Clear pending data
          pendingDebateDataRef.current = null;
        });
      }, 300);
    }
  }, [loadingProgress, isInitializing, countdown]);

  // New state for enhanced debate features
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedCommittee, setSelectedCommittee] = useState('judiciary');
  const [delegates, setDelegates] = useState([]); // All delegates for Model Congress
  const [selectedMUNCommittee, setSelectedMUNCommittee] = useState('');
  const [selectedMUNTopic, setSelectedMUNTopic] = useState('');
  const [customBill, setCustomBill] = useState('');
  const [showBillImport, setShowBillImport] = useState(false);
  const [billImportMethod, setBillImportMethod] = useState('generate'); // 'paste', 'pdf', 'generate'
  const [pdfFile, setPdfFile] = useState(null);
  const [generatedBills, setGeneratedBills] = useState([]);
  const [showBillSelection, setShowBillSelection] = useState(false);
  const [parliamentaryMotions, setParliamentaryMotions] = useState([]);
  const [points, setPoints] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [showEndDebateConfirm, setShowEndDebateConfirm] = useState(false);

  // Set default committee when Model Congress is selected
  useEffect(() => {
    if (format === 'ModelCongress' && !selectedCommittee) {
      setSelectedCommittee('judiciary');
    }
  }, [format, selectedCommittee]);

  const debateTopics = [
    'Social media should be regulated by the government',
    'Remote work is more productive than office work',
    'College education should be free for all students',
    'Video games have positive effects on children',
    'Voting should be mandatory for all citizens',
    'Artificial intelligence poses significant risks to society',
    'Fast food should be banned in schools',
    'Standardized tests are an effective measure of student achievement',
    'The death penalty should be abolished nationwide',
    'Climate change is the most critical issue facing humanity'
  ];

  // Congressional Debate Bills (AI will generate 3 based on topic)
  const congressionalBills = [
    {
      id: 1,
      title: 'Environmental Protection Act',
      category: 'Environment',
      description: 'Legislation to strengthen environmental regulations and promote sustainable practices',
      content: 'This bill proposes increased funding for renewable energy research and stricter emissions standards...'
    },
    {
      id: 2,
      title: 'Education Reform Bill',
      category: 'Education',
      description: 'Comprehensive education reform focusing on student achievement and teacher support',
      content: 'This bill addresses curriculum standards, teacher training programs, and student assessment methods...'
    },
    {
      id: 3,
      title: 'Healthcare Access Act',
      category: 'Healthcare',
      description: 'Legislation to improve healthcare accessibility and affordability for all citizens',
      content: 'This bill proposes healthcare subsidies, expanded Medicaid coverage, and prescription drug price controls...'
    }
  ];

  // Model Congress Committees
  const modelCongressCommittees = [
    { value: 'judiciary', label: 'House Committee on the Judiciary', description: 'Handles legal and constitutional issues' },
    { value: 'appropriations', label: 'Appropriations Committee', description: 'Manages federal spending and budget' },
    { value: 'foreign_affairs', label: 'House Committee on International Relations', description: 'Deals with international relations' },
    { value: 'energy_commerce', label: 'House Committee on Energy and Commerce', description: 'Regulates commerce and energy policy' },
    { value: 'education_labor', label: 'House Committee on Education and the Workforce', description: 'Oversees education and workforce issues' },
    { value: 'homeland_security', label: 'Homeland Security Committee', description: 'Addresses national security concerns' },
    { value: 'banking_housing', label: 'Banking, Housing, and Urban Affairs', description: 'Oversees banking, housing, and urban development' },
    { value: 'armed_services', label: 'House Armed Services Committee', description: 'Handles military and defense matters' },
    { value: 'government_reform', label: 'House Committee on Government Reform', description: 'Oversees government operations and reform' },
    { value: 'science_technology', label: 'House Committee on Science and Technology', description: 'Addresses scientific research and technology policy' },
    { value: 'intelligence', label: 'House Permanent Select Committee on Intelligence', description: 'Oversees intelligence and national security' },
    { value: 'small_business', label: 'House Committee on Small Business and Entrepreneurship', description: 'Supports small business development and entrepreneurship' }
  ];

  // Model UN Committees
  const munCommittees = [
    { value: 'security_council', label: 'Security Council', description: 'Maintains international peace and security' },
    { value: 'general_assembly', label: 'General Assembly', description: 'Main deliberative body of the UN' },
    { value: 'economic_social', label: 'Economic & Social Council', description: 'Addresses economic and social issues' },
    { value: 'human_rights', label: 'Human Rights Council', description: 'Promotes and protects human rights' },
    { value: 'environment', label: 'Environmental Committee', description: 'Addresses environmental challenges' },
    { value: 'disarmament', label: 'Disarmament Committee', description: 'Focuses on arms control and disarmament' }
  ];

  // Parliamentary Motions
  const parliamentaryMotionsList = [
    { name: 'Motion to Open/Close Floor', description: 'Opens or closes debate on a topic' },
    { name: 'Motion to Previous Question', description: 'Forces an immediate vote' },
    { name: 'Motion to Recess/Adjourn', description: 'Pauses or ends the session' },
    { name: 'Motion to Extend/Limit Questioning', description: 'Modifies questioning time' },
    { name: 'Motion to Set Agenda', description: 'Establishes the order of business' },
    { name: 'Motion for Moderated/Unmoderated Caucus', description: 'Requests structured or free discussion' },
    { name: 'Motion to Introduce Draft Resolution', description: 'Presents a resolution for consideration' },
    { name: 'Motion to Amend', description: 'Proposes changes to legislation' },
    { name: 'Motion to Close Debate', description: 'Moves to voting phase' },
    { name: 'Motion to Table/Refer to Committee', description: 'Postpones or refers legislation' }
  ];

  // Parliamentary Points
  const parliamentaryPointsList = [
    { name: 'Point of Order', description: 'Calls out rules violations or seeks clarification' },
    { name: 'Point of Personal Privilege', description: 'Addresses comfort, audibility, or clarity issues' },
    { name: 'Point of Information', description: 'Asks a question about the bill or speech' },
    { name: 'Point of Parliamentary Inquiry', description: 'Seeks clarification on parliamentary procedures' },
    { name: 'Point of Inquiry', description: 'Asks the Chair about procedure' }
  ];

  const debateFormats = [
    { 
      value: 'LD', 
      label: 'Lincoln-Douglas (LD)', 
      description: '1 vs. 1, values-based, philosophical debate focusing on ethical values, justice, and philosophy',
      structure: [
        { name: '1AC - First Affirmative Constructive', code: '1AC', duration: 360, description: 'Opening argument for the resolution. Must establish value, criterion, and 2-3 contentions.' },
        { name: 'CX1 - Cross-Examination of 1AC', code: 'CX1', duration: 180, description: 'Negative questions the affirmative. Questions only, no new arguments.' },
        { name: '1NC - First Negative Constructive', code: '1NC', duration: 420, description: 'Negative case. Must establish value, criterion, and 2-3 contentions.' },
        { name: 'CX2 - Cross-Examination of 1NC', code: 'CX2', duration: 180, description: 'Affirmative questions the negative. Questions only, no new arguments.' },
        { name: '1AR - First Affirmative Rebuttal', code: '1AR', duration: 240, description: 'Affirmative rebuttal. No new arguments - only refutation, extensions, and weighing.' },
        { name: '2NR - Second Negative Rebuttal', code: '2NR', duration: 360, description: 'Negative final rebuttal. No new arguments - collapse and weigh impacts under framework.' },
        { name: '2AR - Second Affirmative Rebuttal', code: '2AR', duration: 180, description: 'Final affirmative rebuttal. No new arguments - final framing and persuasion.' }
      ],
      features: ['No parliamentary motions', 'Cross-examination questioning', 'Framework comparison', 'Value-based evaluation', 'Strict turn-taking', 'No new arguments in rebuttals']
    },
    { 
      value: 'PF', 
      label: 'Public Forum (PF)', 
      description: '2 vs. 2, accessible, policy-driven debate focusing on policy impacts and real-world evidence',
      structure: [
        { name: 'Team A Constructive', duration: 240, description: 'First team opening arguments' },
        { name: 'Team B Constructive', duration: 240, description: 'Second team opening arguments' },
        { name: 'Crossfire (first speakers)', duration: 180, description: 'First speakers question each other' },
        { name: 'Team A Rebuttal', duration: 240, description: 'First team rebuttal' },
        { name: 'Team B Rebuttal', duration: 240, description: 'Second team rebuttal' },
        { name: 'Crossfire (second speakers)', duration: 180, description: 'Second speakers question each other' },
        { name: 'Team A Summary', duration: 120, description: 'First team summary' },
        { name: 'Team B Summary', duration: 120, description: 'Second team summary' },
        { name: 'Grand Crossfire (all debaters)', duration: 180, description: 'All debaters question each other' },
        { name: 'Team A Final Focus', duration: 120, description: 'First team final argument' },
        { name: 'Team B Final Focus', duration: 120, description: 'Second team final argument' }
      ],
      features: ['No motions', 'Crossfire and Grand Crossfire questioning', 'Prep time (2-3 minutes per team)', 'Focus on policy impacts']
    },
    { 
      value: 'Congress', 
      label: 'Congressional Debate', 
      description: 'Chamber simulation of U.S. Congress (15-30 students) focusing on persuasion and legislative advocacy',
      structure: [
        { name: 'Authorship Speech', duration: 180, description: 'Bill author presents the legislation' },
        { name: 'Questioning', duration: 120, description: 'Questions for the author' },
        { name: 'Alternating For/Against Speeches', duration: 180, description: '3-minute speeches alternating sides' },
        { name: 'Questioning after each speech', duration: 120, description: 'Questions for each speaker' },
        { name: 'Vote', duration: 60, description: 'Final vote on the bill' }
      ],
      features: ['Parliamentary motions', 'Points of Information', 'Legislative advocacy focus', 'Chamber simulation']
    },
    { 
      value: 'MUN', 
      label: 'Model United Nations (MUN)', 
      description: 'UN simulation focusing on diplomacy, coalition-building, and global issues',
      structure: [
        { name: 'Roll Call & Agenda Setting', duration: 300, description: 'Attendance and topic selection' },
        { name: 'General Speakers\' List', duration: 180, description: 'Formal speeches on the topic' },
        { name: 'Moderated Caucuses', duration: 300, description: 'Structured discussion periods' },
        { name: 'Unmoderated Caucuses', duration: 600, description: 'Free negotiation time' },
        { name: 'Draft Resolutions', duration: 900, description: 'Resolution writing and introduction' },
        { name: 'Debate + Amendments', duration: 1200, description: 'Resolution debate and amendments' },
        { name: 'Voting on Resolutions', duration: 300, description: 'Final vote on resolutions' }
      ],
      features: ['Diplomatic procedures', 'Coalition building', 'Resolution drafting', 'Global issues focus']
    },
    { 
      value: 'ModelCongress', 
      label: 'Model Congress', 
      description: 'U.S. legislative simulation with committees and chambers focusing on lawmaking realism',
      structure: [
        { name: 'Bill Writing', duration: 1800, description: 'Pre-session bill preparation' },
        { name: 'Committee Debate', duration: 900, description: 'Committee-level bill refinement' },
        { name: 'Chamber Debate', duration: 180, description: 'Alternating For/Against speeches' },
        { name: 'Questioning after speeches', duration: 120, description: 'Questions for speakers' },
        { name: 'Amendments proposed/debated', duration: 600, description: 'Amendment process' },
        { name: 'Final vote on bill', duration: 120, description: 'Chamber vote on final bill' }
      ],
      features: ['Committee structure', 'Amendment process', 'Parliamentary procedures', 'Lawmaking realism']
    }
  ];

  const difficultyLevels = [
    { value: 'easy', label: 'Easy', description: 'Beginner-friendly with simple topics' },
    { value: 'intermediate', label: 'Intermediate', description: 'Balanced challenge for most debaters' },
    { value: 'advanced', label: 'Advanced', description: 'Complex topics and sophisticated arguments' },
    { value: 'expert', label: 'Expert', description: 'Master-level difficulty with nuanced topics' }
  ];

  const debateModes = [
    { value: 'debate', label: 'Debate Me', description: 'Challenge the AI in a competitive debate' },
    { value: 'coach-debate', label: 'Coach Me - Debating', description: 'Get guidance and feedback while debating' },
    { value: 'coach-bill', label: 'Coach Me - Bill Creation', description: 'Get guidance on creating effective legislation' }
  ];

  // Timer effect
  useEffect(() => {
    let interval;
    if (running && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setRunning(false);
    }
    return () => clearInterval(interval);
  }, [running, timer]);

  const randomizeTopic = () => {
    const randomIndex = Math.floor(Math.random() * debateTopics.length);
    setTopic(debateTopics[randomIndex]);
  };

  // Content moderation function
  const validateMessage = (text) => {
    if (!text || !text.trim()) {
      return { valid: false, error: 'Message cannot be empty' };
    }

    // Check for obscene/inappropriate words (basic list - can be expanded)
    const inappropriateWords = [
      // Add common inappropriate words here - keeping minimal for example
      'fuck', 'shit', 'damn', 'bitch', 'asshole', 'bastard', 'crap'
    ];
    
    const lowerText = text.toLowerCase();
    for (const word of inappropriateWords) {
      if (lowerText.includes(word)) {
        return { valid: false, error: 'Please keep your message appropriate and argumentative.' };
      }
    }

    // Check if message is too short to be argumentative (less than 10 characters)
    if (text.trim().length < 10) {
      return { valid: false, error: 'Please provide a more substantial argument (at least 10 characters).' };
    }

    // Check if message is clearly not argumentative (just random characters, single words, etc.)
    const wordCount = text.trim().split(/\s+/).length;
    if (wordCount < 3) {
      return { valid: false, error: 'Please provide a complete argument with multiple words.' };
    }

    // Check for excessive repetition (spam detection)
    const words = text.trim().split(/\s+/);
    const uniqueWords = new Set(words);
    if (words.length > 5 && uniqueWords.size < words.length * 0.3) {
      return { valid: false, error: 'Please avoid repetitive content.' };
    }

    return { valid: true };
  };

  // Countdown function
  const startCountdown = (callback) => {
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setCountdown(null);
          callback();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handlePdfUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      // In a real implementation, you would process the PDF here
      // For now, we'll just store the file reference
    } else {
      alert('Please select a valid PDF file');
    }
  };

  const handleBillImportMethod = (method) => {
    setBillImportMethod(method);
    setShowBillImport(true);
    setCustomBill('');
    setPdfFile(null);
  };

  const handleUseTemplate = (template) => {
    setCustomBill(template);
    setBillImportMethod('paste');
    setShowBillImport(true);
  };

  const selectBillAndStartDebate = async (selectedBillData) => {
    console.log('selectBillAndStartDebate called with bill:', selectedBillData);
    setError('');
    setLoading(true);
    setIsInitializing(true);
    setDebateReady(false);
    setLoadingStage('Initializing debate session...');
    setTargetProgress(50); // Move to 50% when starting debate API call
    try {
      const requestBody = {
        topic: selectedBillData.title,
        side: 'RANDOM',
        format: 'ModelCongress',
        difficulty,
        mode,
        selectedCommittee,
        billImportMethod: 'generate',
        selectedBill: selectedBillData
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/ai-debate/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start debate');
      }
      
      const data = await response.json();
      console.log('Debate started successfully:', data);
      
      setLoadingStage('Setting up delegates and framework...');
      setTargetProgress(80); // Move to 80% after API response
      
      // Store selectedBill from the bill we passed or from response
      if (selectedBillData) {
        setSelectedBill(selectedBillData);
      } else if (data.selectedBill) {
        setSelectedBill(data.selectedBill);
      }
      
      // Store delegates and other data
      if (format === 'ModelCongress' && data.delegates) {
        setDelegates(data.delegates);
      }
      
      setLoadingStage('Finalizing setup...');
      setTargetProgress(90); // Move to 90% after data is stored
      
      // Small delay to show final setup, then move to 100%
      setTimeout(() => {
        setLoadingStage('Almost ready...');
        setTargetProgress(100); // Move to 100% - ready for countdown
      }, 500);
      
      // Store debate data to use when progress reaches 100%
      pendingDebateDataRef.current = {
        sessionId: data.sessionId,
        stage: data.stage,
        stageCode: data.stageCode,
        activeSpeaker: data.activeSpeaker,
        duration: data.duration,
        stageType: data.stageType,
        userSide: data.userSide,
        botSide: data.botSide,
        frameworks: data.frameworks,
        initialTranscript: format === 'ModelCongress' ? data.initialTranscript : null,
        delegates: format === 'ModelCongress' ? data.delegates : null,
        selectedBillData: selectedBillData
      };
    } catch (e) {
      setError(e.message || 'Failed to start debate');
      setIsInitializing(false);
      setLoadingStage('');
      setLoadingProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const startAIDebate = async () => {
    console.log('Start AI Debate clicked!', { format, selectedCommittee, billImportMethod });
    
    if (!user) {
      setError('Please log in to start an AI debate');
      return;
    }

    // Clear any previous errors and reset state
    setError('');
    setSession(null); // Clear previous session
    setIsInitializing(true);
    setDebateReady(false);
    setCountdown(null); // Clear any previous countdown

    // Validate format-specific requirements (simplified for testing)
    if (format === 'Congress' && !selectedBill) {
      setError('Please select a bill to debate');
      return;
    }
    if (format === 'ModelCongress' && !selectedCommittee) {
      console.log('No committee selected, using default');
      setSelectedCommittee('judiciary');
    }
    if (format === 'ModelCongress' && billImportMethod === 'paste' && !customBill.trim()) {
      setError('Please paste your bill content');
      return;
    }
    if (format === 'ModelCongress' && billImportMethod === 'pdf' && !pdfFile) {
      setError('Please upload a PDF file');
      return;
    }
    if (format === 'MUN' && !selectedMUNCommittee) {
      setError('Please select a UN committee');
      return;
    }

      // For Model Congress, automatically select a random bill from the bill bank
    if (format === 'ModelCongress' && (!billImportMethod || billImportMethod === 'generate')) {
      console.log('Selecting random bill for Model Congress...', { selectedCommittee, difficulty, mode });
      setError('');
      setLoading(true);
      setIsInitializing(true); // Show loading screen
      setLoadingStage('Selecting bill...');
      setLoadingProgress(0);
      setTargetProgress(10); // Start at 10%
      try {
        const requestBody = {
          selectedCommittee,
          difficulty,
          mode
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/ai-debate/generate-bills`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to generate bills');
        }
        
        const data = await response.json();
        console.log('Generated bills:', data.bills);
        
        setLoadingStage('Bill generated successfully');
        setTargetProgress(30); // Move to 30% after bill generation
        
        // Randomly select one bill from the generated bills
        if (!data.bills || data.bills.length === 0) {
          throw new Error('No bills available for this committee');
        }
        
        const randomBill = data.bills[Math.floor(Math.random() * data.bills.length)];
        console.log('Randomly selected bill:', randomBill);
        setLoadingStage('Bill selected. Starting debate...');
        setTargetProgress(40); // Move to 40% after bill selection
        
        // Start debate immediately with the randomly selected bill
        await selectBillAndStartDebate(randomBill);
        return;
    } catch (e) {
      console.error('Error selecting bill:', e);
      setError(e.message || 'Failed to select bill');
      setIsInitializing(false);
      setLoading(false);
      setLoadingStage('');
      setLoadingProgress(0);
      return;
    }
    }

    console.log('Starting regular debate...', { topic, side, format, difficulty, mode });
    setError('');
    setLoading(true);
    setLoadingStage('Initializing debate session...');
    setLoadingProgress(0); // Start from 0, will animate
    try {
      const requestBody = {
        topic,
        side,
        format,
        difficulty,
        mode
      };

      // Add format-specific data
      if (format === 'Congress') {
        requestBody.selectedBill = selectedBill;
      } else if (format === 'ModelCongress') {
        requestBody.selectedCommittee = selectedCommittee;
        // Default to generate if no method is selected
        const method = billImportMethod || 'generate';
        requestBody.billImportMethod = method;
        if (method === 'paste') {
          requestBody.customBill = customBill;
        } else if (method === 'pdf') {
          requestBody.pdfFile = pdfFile;
        } else if (method === 'generate') {
          requestBody.generateBill = true;
        }
      } else if (format === 'MUN') {
        requestBody.selectedMUNCommittee = selectedMUNCommittee;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/ai-debate/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start debate');
      }
      
      const data = await response.json();
      
      setLoadingStage('Setting up framework...');
      setTargetProgress(70); // Move to 70% after API response
      
      // Store selectedBill from response for Model Congress
      if (format === 'ModelCongress' && data.selectedBill) {
        setSelectedBill(data.selectedBill);
      }
      
      // Store delegates for Model Congress
      if (format === 'ModelCongress' && data.delegates) {
        setDelegates(data.delegates);
      }
      
      setLoadingStage('Finalizing setup...');
      setTargetProgress(90); // Move to 90% after data is stored
      
      // Small delay to show final setup, then move to 100%
      setTimeout(() => {
        setLoadingStage('Almost ready...');
        setTargetProgress(100); // Move to 100% - ready for countdown
      }, 500);
      
      // Store debate data to use when progress reaches 100%
      pendingDebateDataRef.current = {
        sessionId: data.sessionId,
        stage: data.stage,
        stageCode: data.stageCode,
        activeSpeaker: data.activeSpeaker,
        duration: data.duration,
        stageType: data.stageType,
        userSide: data.userSide,
        botSide: data.botSide,
        frameworks: data.frameworks,
        initialTranscript: null,
        delegates: format === 'ModelCongress' ? data.delegates : null,
        selectedBillData: null
      };
    } catch (e) {
      setError(e.message || 'Failed to start debate');
      setIsInitializing(false);
      setLoadingStage('');
      setLoadingProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const sendTurn = async () => {
    if (!session || !message.trim()) return;
    
    // Validate message content
    const validation = validateMessage(message);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }
    
    setError('');
    setLog(l => [...l, { role: 'user', text: message }]);
    const toSend = message;
    setMessage('');
    setRunning(false);
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/ai-debate/turn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: session,
          userMessage: toSend
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send turn');
      }
      
      const data = await response.json();
      
      // For Model Congress, the backend generates delegate speeches and adds them to the session transcript
      // We need to fetch the updated transcript or the backend should return new entries
      // For now, add the bot response - delegate entries will be shown when they come through
      if (format === 'ModelCongress') {
        // The backend generates delegate speeches internally and stores them in the session
        // We'll add the bot entry here, but delegate entries will need to be retrieved separately
        // or the backend should return all new transcript entries
        const botEntry = { role: 'bot', text: data.botText || '' };
        setLog(l => [...l, botEntry]);
        
        // TODO: The backend should return new transcript entries including delegate speeches
        // For now, delegates will appear in the active delegates list once they speak
        // but their speeches won't show until we fetch the full transcript
      } else {
        setLog(l => [...l, { role: 'bot', text: data.botText }]);
      }
      setStage(data.stage);
      setStageCode(data.stageCode || null);
      setActiveSpeaker(data.activeSpeaker || null);
      setStageDuration(data.duration || null);
      setStageType(data.stageType || null);
      setFrameworks(data.frameworks || null);
      setTimer(data.duration || 90);
      setRunning(true);
    } catch (e) {
      setError(e.message || 'Failed to send turn');
    } finally {
      setLoading(false);
    }
  };

  const finishDebate = async () => {
    if (!session) return;
    
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/ai-debate/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: session
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get feedback');
      }
      
      const data = await response.json();
      setLog(l => [
        ...l, 
        { role: 'system', text: '— Match Summary —' }, 
        { role: 'system', text: data.feedback }
      ]);
      setRunning(false);
    } catch (e) {
      setError(e.message || 'Failed to get feedback');
    } finally {
      setLoading(false);
    }
  };

  const resetDebate = () => {
    // Reset all debate state
    setSession(null);
    setStage('');
    setStageCode(null);
    setActiveSpeaker(null);
    setStageDuration(null);
    setStageType(null);
    setUserSide(null);
    setBotSide(null);
    setFrameworks(null);
    setLog([]);
    setTimer(90);
    setRunning(false);
    setLoading(false);
    setError('');
    setMessage('');
    setIsInitializing(false);
    setCountdown(null);
    setDebateReady(false);
    setSelectedBill(null); // Clear selected bill for Model Congress
    setGeneratedBills([]); // Clear generated bills
    setShowBillSelection(false); // Hide bill selection
    setDelegates([]); // Clear delegates
    setIsPaused(false);
    setShowEndDebateConfirm(false);
    setLoadingStage('');
    setLoadingProgress(0);
    setTargetProgress(0);
    pendingDebateDataRef.current = null; // Clear pending debate data
  };

  const handleEndDebate = () => {
    setShowEndDebateConfirm(true);
  };

  const confirmEndDebate = () => {
    resetDebate();
    setShowEndDebateConfirm(false);
  };

  const resetDebate_OLD = () => {
    setSession(null);
    setStage('');
    setStageCode(null);
    setActiveSpeaker(null);
    setStageDuration(null);
    setStageType(null);
    setUserSide(null);
    setBotSide(null);
    setFrameworks(null);
    setLog([]);
    setTimer(90);
    setRunning(false);
    setError('');
    setMessage('');
    setShowAIDebate(false);
    setSelectedMode(null);
    setIsInitializing(false);
    setCountdown(null);
    setDebateReady(false);
  };

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    setShowAIDebate(false);
    setShowFriendDebate(false);
    setShowRandomDebate(false);
    
    switch (mode) {
      case 'ai':
        setShowAIDebate(true);
        break;
      case 'friend':
        setShowFriendDebate(true);
        break;
      case 'random':
        setShowRandomDebate(true);
        break;
      default:
        break;
    }
  };

  const renderModeSelection = () => {
    const debateModes = [
      {
        name: "AI Debate",
        description: "Challenge our advanced AI in a one-on-one debate. Perfect for practice and skill development.",
        Icon: Bot,
        onClick: () => handleModeSelect('ai'),
        cta: "Start AI Debate",
        gradientFrom: "from-primary-500",
        gradientTo: "to-primary-700",
        className: "",
      },
      {
        name: "Friend Debate",
        description: "Debate against your friends in private rooms. Invite them and start debating together.",
        Icon: Users,
        onClick: () => handleModeSelect('friend'),
        cta: "Debate Friends",
        gradientFrom: "from-secondary-500",
        gradientTo: "to-secondary-700",
        className: "",
      },
      {
        name: "Random Match",
        description: "Get matched with other debaters for competitive matches. Test your skills against the community.",
        Icon: Shuffle,
        onClick: () => handleModeSelect('random'),
        cta: "Find Match",
        gradientFrom: "from-success-500",
        gradientTo: "to-success-700",
        className: "",
      },
    ];

    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Choose Your Debate Mode
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Select how you'd like to debate today
          </p>
        </div>

        <BentoGrid className="md:grid-rows-1">
          {debateModes.map((mode) => (
            <BentoCard
              key={mode.name}
              name={mode.name}
              description={mode.description}
              Icon={mode.Icon}
              onClick={mode.onClick}
              cta={mode.cta}
              className={mode.className}
              gradientFrom={mode.gradientFrom}
              gradientTo={mode.gradientTo}
              background={
                <div className={cn(
                  "absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-300",
                  `bg-gradient-to-br ${mode.gradientFrom} ${mode.gradientTo}`
                )} />
              }
            />
          ))}
        </BentoGrid>
      </div>
    );
  };

  const renderAIDebate = () => {
    console.log('renderAIDebate called with session:', session, 'showBillSelection:', showBillSelection, 'isInitializing:', isInitializing, 'countdown:', countdown);
    const isActiveDebateSession = debateReady && session && (format === 'ModelCongress' || format === 'PF' || format === 'LD');
    
    // Show loading screen first if initializing (with progress bar)
    // Show countdown after progress reaches 100%
    if (isInitializing && countdown === null) {
      return (
        <div className={isActiveDebateSession ? "w-full h-full flex items-center justify-center" : "max-w-4xl mx-auto"}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 w-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-6"></div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Preparing Debate...</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">{loadingStage || 'Setting up your debate session'}</p>
              
              {/* Progress Bar */}
              <div className="w-full max-w-md mx-auto">
                <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                  <div 
                    className="bg-primary-600 h-full rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${Math.min(loadingProgress, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Show countdown after progress completes
    if (countdown !== null) {
      return (
        <div className={isActiveDebateSession ? "w-full h-full flex items-center justify-center" : "max-w-4xl mx-auto"}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 w-full">
            <div className="text-center">
              <div className="text-9xl font-bold text-primary-600 mb-4 animate-pulse">
                {countdown}
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Get Ready!</h3>
              <p className="text-gray-600 dark:text-gray-300">Debate starting in {countdown}...</p>
            </div>
          </div>
        </div>
      );
    }
    
    return (
    <div className={isActiveDebateSession ? "w-full h-full" : "max-w-4xl mx-auto"} style={isActiveDebateSession ? { height: '100%' } : {}}>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setShowAIDebate(false)}
          className="flex items-center text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
        >
          <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
          Back to Debate Modes
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AI Debate</h1>
      </div>

      {!session ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column - Configuration */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Debate Configuration</h3>
              
              {/* Topic Selection - Hidden for Model Congress */}
              {format !== 'ModelCongress' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Debate Topic
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter debate topic..."
                    />
                    <button
                      onClick={randomizeTopic}
                      className="px-3 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      <Shuffle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Side Selection - Hidden for Model Congress */}
              {format !== 'ModelCongress' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Side
                  </label>
                  <select
                    value={side}
                    onChange={(e) => setSide(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="RANDOM">Random</option>
                    <option value="PRO">Pro</option>
                    <option value="CON">Con</option>
                  </select>
                </div>
              )}

              {/* Format Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Debate Format
                </label>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  {debateFormats.map(fmt => (
                    <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
                  ))}
                </select>
                
                {/* Format Details */}
                {format && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      {debateFormats.find(f => f.value === format)?.description}
                    </p>
                    
                    {/* Structure Preview */}
                    <div className="mb-3">
                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Structure:</h4>
                      <div className="space-y-1">
                        {debateFormats.find(f => f.value === format)?.structure.slice(0, 3).map((stage, idx) => (
                          <div key={idx} className="text-xs text-gray-600 dark:text-gray-300">
                            • {stage.name} ({Math.floor(stage.duration / 60)}m)
                          </div>
                        ))}
                        {debateFormats.find(f => f.value === format)?.structure.length > 3 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            +{debateFormats.find(f => f.value === format)?.structure.length - 3} more stages
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Features */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Features:</h4>
                      <div className="flex flex-wrap gap-1">
                        {debateFormats.find(f => f.value === format)?.features.map((feature, idx) => (
                          <span key={idx} className="text-xs bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 px-2 py-1 rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Format-Specific Configuration */}
              {format === 'Congress' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Bill to Debate
                  </label>
                  <div className="space-y-2">
                    {congressionalBills.map(bill => (
                      <button
                        key={bill.id}
                        onClick={() => setSelectedBill(bill)}
                        className={`w-full text-left p-3 rounded-md border transition-colors ${
                          selectedBill?.id === bill.id
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600'
                        }`}
                      >
                        <div className="font-medium text-gray-900 dark:text-white">{bill.title}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">{bill.category}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{bill.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {format === 'ModelCongress' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Committee Selection
                  </label>
                  <select
                    value={selectedCommittee}
                    onChange={(e) => setSelectedCommittee(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select a committee...</option>
                    {modelCongressCommittees.map(committee => (
                      <option key={committee.value} value={committee.value}>{committee.label}</option>
                    ))}
                  </select>
                  
                  {selectedCommittee && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Bill Option
                      </label>
                      <div className="space-y-2">
                        <button
                          onClick={() => handleBillImportMethod('generate')}
                          className={`w-full text-left p-3 rounded-md border transition-colors ${
                            billImportMethod === 'generate'
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600'
                          }`}
                        >
                          <div className="font-medium text-gray-900 dark:text-white">Generate AI Bill</div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">AI will create a bill with random topic based on committee</div>
                        </button>
                        <button
                          onClick={() => handleBillImportMethod('paste')}
                          className={`w-full text-left p-3 rounded-md border transition-colors ${
                            billImportMethod === 'paste'
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600'
                          }`}
                        >
                          <div className="font-medium text-gray-900 dark:text-white">Paste Bill Content</div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">Paste your own bill text directly</div>
                        </button>
                        <button
                          onClick={() => handleBillImportMethod('pdf')}
                          className={`w-full text-left p-3 rounded-md border transition-colors ${
                            billImportMethod === 'pdf'
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600'
                          }`}
                        >
                          <div className="font-medium text-gray-900 dark:text-white">Import PDF Bill</div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">Upload a PDF file containing your bill</div>
                        </button>
                      </div>
                      
                      {/* Bill Import Interface */}
                      {showBillImport && (
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                     {billImportMethod === 'paste' && (
                             <div>
                               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                 Paste Your Bill Content
                               </label>
                               <textarea
                                 value={customBill}
                                 onChange={(e) => setCustomBill(e.target.value)}
                                 placeholder="Paste your bill content here... (You can use the Model Congress template format)"
                                 className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                                 rows={8}
                               />
                               <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                 💡 Tip: You can paste content from the Model Congress template or any other bill format
                               </div>
                               
                               {/* Bill Template Component */}
                               <div className="mt-4">
                                 <BillTemplate onUseTemplate={handleUseTemplate} />
                               </div>
                             </div>
                           )}
                          
                          {billImportMethod === 'pdf' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Upload PDF Bill
                              </label>
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={handlePdfUpload}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                              />
                              {pdfFile && (
                                <div className="mt-2 p-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-sm">
                                  ✅ PDF uploaded: {pdfFile.name}
                                </div>
                              )}
                              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                💡 Tip: The AI will extract and process the bill content from your PDF
                              </div>
                            </div>
                          )}
                          
                          {billImportMethod === 'generate' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                AI Bill Generation
                              </label>
                              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-sm">
                                🤖 AI will generate a comprehensive bill with a random topic relevant to the {modelCongressCommittees.find(c => c.value === selectedCommittee)?.label}
                              </div>
                              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                💡 Tip: The AI will create a properly formatted bill following Model Congress standards
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {format === 'MUN' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    UN Committee
                  </label>
                  <select
                    value={selectedMUNCommittee}
                    onChange={(e) => setSelectedMUNCommittee(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select a committee...</option>
                    {munCommittees.map(committee => (
                      <option key={committee.value} value={committee.value}>{committee.label}</option>
                    ))}
                  </select>
                  
                  {selectedMUNCommittee && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Committee Topic
                      </label>
                      <div className="space-y-2">
                        <div className="text-sm text-gray-600 dark:text-gray-300 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                          <strong>AI will generate 3 topics based on your selected committee:</strong>
                          <ul className="mt-2 space-y-1">
                            <li>• Topic 1: [AI Generated]</li>
                            <li>• Topic 2: [AI Generated]</li>
                            <li>• Topic 3: [AI Generated]</li>
                          </ul>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            You'll select your preferred topic when the debate starts.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Difficulty Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Difficulty Level
                </label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  {difficultyLevels.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>


              <button
                onClick={() => {
                  console.log('Button clicked!', { format, selectedCommittee, billImportMethod });
                  startAIDebate();
                }}
                disabled={loading}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" />
                    Start AI Debate
                  </>
                )}
              </button>
            </div>

            {/* Right Column - Preview */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Debate Preview</h3>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="space-y-3">
                  {format !== 'ModelCongress' && (
                    <div>
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Topic:</span>
                      <p className="text-gray-900 dark:text-white">{topic}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Format:</span>
                    <p className="text-gray-900 dark:text-white">{debateFormats.find(f => f.value === format)?.label}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Difficulty:</span>
                    <p className="text-gray-900 dark:text-white">{difficultyLevels.find(d => d.value === difficulty)?.label}</p>
                  </div>
                  {/* Format-Specific Preview */}
                  {format === 'Congress' && selectedBill && (
                    <div>
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Selected Bill:</span>
                      <p className="text-gray-900 dark:text-white">{selectedBill.title}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{selectedBill.category}</p>
                    </div>
                  )}
                  
                  {format === 'ModelCongress' && selectedCommittee && (
                    <div>
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Committee:</span>
                      <p className="text-gray-900 dark:text-white">{modelCongressCommittees.find(c => c.value === selectedCommittee)?.label}</p>
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Bill Method:</span>
                      <p className="text-gray-900 dark:text-white">
                        {billImportMethod === 'generate' && 'AI Generated Bill'}
                        {billImportMethod === 'paste' && 'Pasted Bill Content'}
                        {billImportMethod === 'pdf' && 'PDF Import'}
                        {!billImportMethod && 'AI Generated Bill (Default)'}
                      </p>
                      {billImportMethod === 'pdf' && pdfFile && (
                        <div>
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">PDF File:</span>
                          <p className="text-gray-900 dark:text-white text-sm">{pdfFile.name}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {format === 'MUN' && selectedMUNCommittee && (
                    <div>
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">UN Committee:</span>
                      <p className="text-gray-900 dark:text-white">{munCommittees.find(c => c.value === selectedMUNCommittee)?.label}</p>
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Topics:</span>
                      <p className="text-gray-900 dark:text-white">AI Generated (3 options)</p>
                    </div>
                  )}
                </div>
                
                {/* Parliamentary Procedures Preview */}
                {(format === 'Congress' || format === 'ModelCongress' || format === 'MUN') && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Available Procedures:</h4>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Motions:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {parliamentaryMotionsList.slice(0, 3).map((motion, idx) => (
                            <span key={idx} className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                              {motion.name}
                            </span>
                          ))}
                          <span className="text-xs text-gray-500 dark:text-gray-400">+{parliamentaryMotionsList.length - 3} more</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Points:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {parliamentaryPointsList.slice(0, 3).map((point, idx) => (
                            <span key={idx} className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                              {point.name}
                            </span>
                          ))}
                          <span className="text-xs text-gray-500 dark:text-gray-400">+{parliamentaryPointsList.length - 3} more</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : showBillSelection ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          {console.log('Showing bill selection interface with', generatedBills.length, 'bills')}
          {console.log('Generated bills array:', generatedBills)}
          {console.log('showBillSelection state:', showBillSelection)}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Select a Bill to Debate</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Committee: {modelCongressCommittees.find(c => c.value === selectedCommittee)?.label}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                ⚠️ You will debate <strong>one bill</strong> per session. After completing this debate, you can start a new Model Congress debate.
              </p>
            </div>
            <button
              onClick={() => setShowBillSelection(false)}
              className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
            >
              ← Back to Configuration
            </button>
          </div>

          <div className="grid gap-4">
            {generatedBills.map((bill, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-primary-300 dark:hover:border-primary-600 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{bill.title}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{bill.description}</p>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      <strong>Category:</strong> {bill.category}
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>Summary:</strong> {bill.summary}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Select This Bill button clicked for bill:', bill.title);
                      selectBillAndStartDebate(bill);
                    }}
                    disabled={loading}
                    className="ml-4 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ pointerEvents: loading ? 'none' : 'auto' }}
                  >
                    {loading ? 'Starting...' : 'Select This Bill'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : isInitializing || countdown !== null ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12">
          <div className="text-center">
            {isInitializing ? (
              <>
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Preparing Debate...</h3>
                <p className="text-gray-600 dark:text-gray-300">Setting up your debate session</p>
              </>
            ) : countdown !== null ? (
              <>
                <div className="text-9xl font-bold text-primary-600 mb-4 animate-pulse">
                  {countdown}
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Get Ready!</h3>
                <p className="text-gray-600 dark:text-gray-300">Debate starting in {countdown}...</p>
              </>
            ) : null}
          </div>
        </div>
      ) : debateReady && session ? (
        <div className={isActiveDebateSession ? "h-[calc(100vh-5rem)] flex flex-col w-full" : "h-[calc(100vh-5rem)] flex flex-col w-full max-w-4xl mx-auto"}>
          {console.log('Rendering DebateLayout with session:', session, 'stage:', stage, 'log length:', log.length)}
          <DebateLayout
            format={format}
            topic={topic}
            stage={stage}
            stageCode={stageCode}
            activeSpeaker={activeSpeaker}
            duration={stageDuration}
            stageType={stageType}
            timer={timer}
            log={log}
            message={message}
            setMessage={setMessage}
            sendTurn={sendTurn}
            loading={loading}
            error={error}
            userSide={userSide}
            botSide={botSide}
            frameworks={frameworks}
            selectedCommittee={selectedCommittee}
            selectedBill={selectedBill}
            delegates={delegates}
            isPaused={isPaused}
            onPauseToggle={() => setIsPaused(!isPaused)}
            onEndDebate={handleEndDebate}
            onProposeMotion={(motion) => {
              console.log('Motion proposed:', motion);
              // TODO: Send motion to backend
            }}
            onRaisePoint={(point) => {
              console.log('Point raised:', point);
              // TODO: Send point to backend
            }}
          />
        </div>
      ) : null}

    </div>
    );
  };

  const renderFriendDebate = () => (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setShowFriendDebate(false)}
          className="flex items-center text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
        >
          <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
          Back to Debate Modes
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Friend Debate</h1>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
        <Users className="w-16 h-16 text-primary-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Friend Debate Coming Soon</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Challenge your friends in private debate rooms. Create rooms, invite friends, and debate together.
        </p>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-w-md mx-auto">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Features Coming:</h4>
          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
            <li>• Create private debate rooms</li>
            <li>• Invite friends via link</li>
            <li>• Real-time debate with friends</li>
            <li>• Debate history and statistics</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderRandomDebate = () => (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setShowRandomDebate(false)}
          className="flex items-center text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
        >
          <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
          Back to Debate Modes
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Random Matchmaking</h1>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
        <Shuffle className="w-16 h-16 text-primary-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Random Matchmaking Coming Soon</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Get matched with other debaters for competitive matches. Test your skills against the community.
        </p>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-w-md mx-auto">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Features Coming:</h4>
          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
            <li>• Skill-based matchmaking</li>
            <li>• Quick match finding</li>
            <li>• Competitive rankings</li>
            <li>• Tournament integration</li>
          </ul>
        </div>
      </div>
    </div>
  );

  // Check if we're in an active debate session (for full-screen layout)
  const isActiveDebate = debateReady && session && (format === 'ModelCongress' || format === 'PF' || format === 'LD');
  
  return (
    <div className={`min-h-screen ${isActiveDebate ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'} ${isActiveDebate ? '' : 'pt-24 pb-8'}`}>
      {/* End Debate Confirmation Dialog */}
      {showEndDebateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              End Debate?
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to end this debate? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowEndDebateConfirm(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmEndDebate}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
              >
                End Debate
              </button>
            </div>
          </div>
        </div>
      )}

      {isActiveDebate ? (
        <div className="w-full h-[calc(100vh-5rem)] flex flex-col">
          {renderAIDebate()}
        </div>
      ) : (
        <div className="container mx-auto px-4">
          {!showAIDebate && !showFriendDebate && !showRandomDebate ? (
            renderModeSelection()
          ) : showAIDebate ? (
            renderAIDebate()
          ) : showFriendDebate ? (
            renderFriendDebate()
          ) : (
            renderRandomDebate()
          )}
        </div>
      )}
    </div>
  );
};

export default Debate; 